import {useCertificateCapabilitiesQuery} from './useCertificatesQuery';
import {CertificateWriteCapabilities} from '../services/CertificatesService';

// FNP-1193 — THE CAPABILITY-DRIVEN HIDING DECISION.
//
// Follows `zones/hooks/useZoneWriteCapabilities` and
// `dns-records/hooks/useRecordWriteCapabilities` exactly — same query, same
// default, same reasoning — because the rule is the same rule and three modules
// deriving it differently is how they end up disagreeing.
//
// ⚠️ THE DEFAULT IS "NOT PERMITTED", IN EVERY UNKNOWN CASE.
//
// Loading, errored, connection missing, connection not ACTIVE, capability
// snapshot absent — all of them hide the control. The alternative fails in the
// worse direction: a control that appears optimistically and then errors at
// submit tells a tenant their credential is broken, when the truth is that
// Flexi should never have offered the action.
//
// ⚠️ FNP-1280 — THE GATE IS `certificateCapabilities.canRequest`, AND IT USED
// TO BE `writable`. That was the defect this ticket fixed.
//
// This is not the DNS capability snapshot. `ProviderConnectionCapabilities` has
// no ACM field at all, so the tempting reach is `canWriteRecords` — and that
// would be a proxy for the wrong thing. But so was `writable`: it is the DNS
// declaration, and the backend used to derive `canRequest` from it and nothing
// else (`certificate/src/main.ts` read `connection.writable === true` once and
// fed the one answer to both `canRequest` and `canDelete`). A tenant grants
// `route53:ChangeResourceRecordSets` and `acm:RequestCertificate` as separate
// statements in their own role, so a tenant holding the first and not the
// second saw a Request button that failed at ACM after they filled in the form.
//
// The connection now carries a sibling `certificateCapabilities` block, and
// this hook reads it. So it hides exactly the controls the backend would
// refuse, with no guessing between the two.
//
// ⚠️ IT STILL CANNOT PROVE A REQUEST WILL SUCCEED, and pretending otherwise is
// the failure to avoid. The declaration is made BY THE TENANT, never probed:
// verifying it needs `iam:SimulatePrincipalPolicy` run in the account that owns
// the principal, which is the tenant's own, so Flexi has no way to check. A
// tenant who declares `canRequest: true` on a role that lacks the grant passes
// this gate and is still refused by ACM.
//
// That is why the module keeps a runtime error path rather than treating this
// gate as the last word: a PERMISSION_DENIED refusal now arrives CLASSIFIED
// from the backend — rather than as prose the console had to pattern-match —
// and gets copy naming `acm:RequestCertificate`. See writeErrorMessages.
//
// ⚠️ THE TENANT-FACING ROLE INSTRUCTIONS ARE A SEPARATE TICKET (FNP-1276).
// Until they name the ACM actions, a tenant cannot DECLARE a grant they were
// never told to create, so this gate correctly hides the control for every
// connection that predates it. That is honest, not broken — but it does mean
// the capability is close to inert until that copy lands.
export const useCertificateWriteCapabilities = (connectionId?: string) => {

    const {
        data: capabilities,
        isLoading,
        isError,
    } = useCertificateCapabilitiesQuery(connectionId);

    // The query resolves null for "no snapshot" (React Query v5 forbids
    // undefined); both mean the same thing to every caller below.
    const resolved: CertificateWriteCapabilities | undefined = capabilities ?? undefined;

    // Not merely `!capabilities`: a snapshot that arrived is still unusable
    // while the query is in flight or after it failed.
    const known = Boolean(connectionId) && !isLoading && !isError && Boolean(resolved);

    return {
        capabilities: resolved,
        // True only when Flexi actually knows what this credential can do.
        capabilitiesKnown: known,
        loading: Boolean(connectionId) && isLoading,
        // ⚠️ WHAT EVERY WRITE CONTROL IN THIS MODULE GATES ON.
        //
        // Named for what it establishes rather than for what a caller wishes it
        // established: this connection passes the backend's own gate. It is not
        // "this connection can issue a certificate" — nothing available to the
        // console can establish that, because the grant is declared, not probed.
        canRequestCertificates: known && resolved!.canRequestCertificates === true,
        // FNP-1280 — reported separately because it IS a separate grant.
        // `acm:DeleteCertificate` is its own statement in the tenant's role, so
        // a tenant may hold one and not the other.
        canDeleteCertificates: known && resolved!.canDeleteCertificates === true,
    };
};
